/**
 * ページめくり音（FR-SND-01・既定 ON）。
 *
 * 既定では音源ファイルを同梱せず、Web Audio で紙の擦れ音を合成する
 * （配布物が増えず、素材のライセンス問題も生じない）。
 * soundFile を指定した場合はその音声を取得して鳴らす（差替え要件）。
 *
 * 自動再生ポリシー: AudioContext はユーザー操作の文脈（めくり操作）で初めて作る。
 * それでも拒否される環境があるため、失敗は常に握りつぶしてエラーを出さない。
 */
import { assetUrl } from '../core/asset-base';
import type { DisposeRegistry } from '../core/dispose-registry';

/** 合成音の長さ（秒）と音量。 */
const DURATION = 0.18;
const VOLUME = 0.25;

/** 帯域通過の中心周波数（Hz）— 低いほど厚い紙の音になる。 */
const FILTER_FROM = 900;
const FILTER_TO = 2800;

interface AudioApi {
  AudioContext?: typeof AudioContext;
  webkitAudioContext?: typeof AudioContext;
}

export interface FlipSoundOptions {
  enabled: boolean;
  /** 音源ファイルの URL（空なら合成音）。 */
  file: string;
  /** 側面資産の基準 URL。 */
  assetBase: string;
}

export class FlipSound {
  private context: AudioContext | null = null;
  private noise: AudioBuffer | null = null;
  private sample: AudioBuffer | null = null;
  private loading: Promise<void> | null = null;
  private disposed = false;

  constructor(
    private readonly view: Window,
    private readonly registry: DisposeRegistry,
    private readonly options: FlipSoundOptions,
  ) {}

  get enabled(): boolean {
    return this.options.enabled;
  }

  setEnabled(enabled: boolean): void {
    this.options.enabled = enabled;
  }

  /** めくり音を鳴らす。鳴らせない環境では黙って何もしない。 */
  play(): void {
    if (!this.options.enabled || this.disposed) return;
    const context = this.ensureContext();
    if (!context) return;

    // 自動再生ポリシーで停止していることがある（拒否は無視してよい）。
    if (context.state === 'suspended') void context.resume().catch(() => undefined);

    if (this.options.file) {
      void this.loadSample(context).then(() => this.emit(context, this.sample));
      return;
    }
    this.emit(context, this.noise ?? this.createNoise(context));
  }

  dispose(): void {
    this.disposed = true;
    void this.context?.close().catch(() => undefined);
    this.context = null;
    this.noise = null;
    this.sample = null;
  }

  /** AudioContext を遅延生成する（作れない環境では null）。 */
  private ensureContext(): AudioContext | null {
    if (this.context) return this.context;
    const api = this.view as unknown as AudioApi;
    const Ctor = api.AudioContext ?? api.webkitAudioContext;
    if (!Ctor) return null;
    try {
      this.context = new Ctor();
      this.registry.add('other', () => {
        void this.context?.close().catch(() => undefined);
        this.context = null;
      });
      return this.context;
    } catch {
      return null;
    }
  }

  /** 紙が擦れる音（減衰するノイズ）を 1 度だけ作る。 */
  private createNoise(context: AudioContext): AudioBuffer {
    const frames = Math.floor(context.sampleRate * DURATION);
    const buffer = context.createBuffer(1, frames, context.sampleRate);
    const samples = buffer.getChannelData(0);
    for (let i = 0; i < frames; i += 1) {
      const t = i / frames;
      // 立ち上がりを速く、後半をなだらかに落として「シャッ」という質感にする。
      samples[i] = (Math.random() * 2 - 1) * (1 - t) ** 2;
    }
    this.noise = buffer;
    return buffer;
  }

  /** 差替え用の音源を取得してデコードする（失敗しても黙って合成音へは戻さない）。 */
  private loadSample(context: AudioContext): Promise<void> {
    if (this.sample) return Promise.resolve();
    if (this.loading) return this.loading;

    const url = assetUrl(this.options.file, this.options.assetBase);
    this.loading = fetch(url)
      .then((response) => response.arrayBuffer())
      .then((data) => context.decodeAudioData(data))
      .then((buffer) => {
        this.sample = buffer;
      })
      .catch(() => undefined)
      .finally(() => {
        this.loading = null;
      });
    return this.loading;
  }

  /** 音を 1 回鳴らす。 */
  private emit(context: AudioContext, buffer: AudioBuffer | null): void {
    if (!buffer || this.disposed) return;

    const source = context.createBufferSource();
    source.buffer = buffer;

    const gain = context.createGain();
    gain.gain.value = VOLUME;

    if (this.options.file) {
      source.connect(gain);
    } else {
      // 合成音は帯域を絞り、中心周波数を上へ動かして紙をめくる質感に近づける。
      const filter = context.createBiquadFilter();
      filter.type = 'bandpass';
      filter.Q.value = 0.8;
      const now = context.currentTime;
      filter.frequency.setValueAtTime(FILTER_FROM, now);
      filter.frequency.linearRampToValueAtTime(FILTER_TO, now + DURATION);
      source.connect(filter);
      filter.connect(gain);
    }

    gain.connect(context.destination);
    source.start();
    source.onended = () => {
      source.disconnect();
      gain.disconnect();
    };
  }
}
